import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Service, { isServiceReady } from '../services/Service';
import UserService from '../services/UserService';
import { Field } from '../types/common';
import { REGEX_PASSWORD } from '../services/models/user';
import { useMutation } from '@tanstack/react-query';
import { Button, Input } from '@follow/ui';

const ResetPassword = () => {
  const [password, setPassword] = useState<Field>({ value: '' });
  const [confirmPassword, setConfirmPassword] = useState<Field>({ value: '' });
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const token = searchParams.get('token');

  const resetPasswordMutation = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      UserService.resetPassword(token, password),
    onSuccess: () => {
      navigate('/login?reset=true', { replace: true });
    },
    onError: (e: any) => {
      const messages: Record<string, string> = {
        INVALID_OR_EXPIRED_TOKEN: 'Le lien de réinitialisation est invalide ou a expiré.',
        INVALID_PASSWORD_FORMAT: 'Le mot de passe doit contenir au moins 8 caractères, 1 majuscule, 1 minuscule et 1 chiffre.',
      };
      toast.error(messages[e.code] || 'Une erreur est survenue.');
    },
  });

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword({ value: e.target.value });
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword({ value: e.target.value });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const form = event.currentTarget;
      form.requestSubmit();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (resetPasswordMutation.isPending) return;

    if (password.value !== confirmPassword.value) {
      toast.error('Les mots de passe ne correspondent pas.');
      return;
    }

    if (!REGEX_PASSWORD.test(password.value!)) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères, 1 majuscule, 1 minuscule et 1 chiffre.');
      return;
    }

    resetPasswordMutation.mutate({ token: token!, password: password.value! });
  };

  useEffect(() => {
    if (!isServiceReady) {
      Service.init(() => {
        localStorage.removeItem('token');
        navigate('/login');
      });
    }
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  if (!token) return null;

  return (
    <div className="flex min-h-screen bg-off-white overscroll-none overflow-hidden">
      {/* LEFT PANEL */}
      <div className="w-full lg:w-[35%] bg-secondary-900 flex flex-col">
        <div className="p-8">
          <a href={process.env.REACT_APP_LANDING_URL || '/'}>
            <img src="/logo_full_light.svg" alt="Follow" className="h-7 w-auto" />
          </a>
        </div>

        <div className="flex-1 flex items-center justify-center px-5 lg:px-16">
          <form className="w-full max-w-[448px]" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
            <h1 className="text-white/80 typo-h1">Nouveau mot de passe</h1>
            <p className="text-white/70 typo-body-lg mt-2">Choisissez un nouveau mot de passe</p>

            {/* Password */}
            <div className="mt-8">
              <label className="block text-white/70 typo-small mb-2" htmlFor="password">
                Mot de passe
              </label>
              <Input
                tone="light"
                id="password"
                type="password"
                placeholder="••••••••"
                value={password.value}
                onChange={handlePasswordChange}
              />
            </div>

            {/* Confirm Password */}
            <div className="mt-5">
              <label className="block text-white/70 typo-small mb-2" htmlFor="confirmPassword">
                Confirmer le mot de passe
              </label>
              <Input
                tone="light"
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword.value}
                onChange={handleConfirmPasswordChange}
              />
            </div>

            <p className="mt-3 text-white/40 typo-small">
              8 caractères minimum, 1 majuscule, 1 minuscule, 1 chiffre
            </p>

            {/* Submit */}
            <div className="mt-8">
              <Button
                type="submit"
                variant="primary"
                tone="light"
                size="lg"
                className="w-full"
                disabled={!password.value || !confirmPassword.value}
                loading={resetPasswordMutation.isPending}
              >
                Réinitialiser le mot de passe
              </Button>
            </div>

            {/* Back to login */}
            <p className="mt-6 text-center text-white/70 typo-small">
              <Link to="/login" className="text-white/90 font-medium hover:underline">
                Retour à la connexion
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="hidden lg:flex lg:w-[65%] relative bg-secondary-900 border-l border-secondary-800 overflow-hidden lg:flex-col items-center justify-center">
        <img
          src="/signin-background.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/15" />

        <div className="relative z-10 flex flex-col justify-center items-center h-full w-full gap-12">
          <h2 className="text-white typo-display text-center leading-tight">
            <span className="block">{'Transformez vos stations en'}</span>
            <span className="block text-primary-500">{' produits connectés'}</span>
          </h2>
          <p className="text-white typo-h3 text-center">
            Une solution intégrée pour piloter vos stations à distance.
          </p>

          <div className="flex justify-end -mt-32">
            <img
              src="/signin-lock.png"
              alt="Station Follow"
              className="w-[580px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
